// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useState } from "react";
import { SwapJSON } from "../channel/Swap";
import { TypedJSON } from "typedjson";
import { Container, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AppContext } from "../AppContext";
import StellarAsset from "../api/types/stellarAsset";
import { getTokenCode } from "./forms/AmountAndAssetForm";
import EthereumAsset from "../api/types/ethereumAsset";
import { NervosAsset } from "../api/types/nervosAsset";
import { getExplorerURL } from "../constants/Constants";
import { formatBaseUnits } from "../web3/balanceUtils";

// Decimals of the assets this frontend trades, for history entries whose
// decimals the backend cannot supply.
const knownDecimals = new Map<string, number>([
  ["ETH", 18],
  ["USDC", 6],
  ["CKB", 8],
]);

// getExplorerURL throws for a chain id it has no explorer for. Older swap
// entries in localStorage can carry such an id; throwing while rendering the
// table took the whole page down (mainnet, 2026-09-08), so the link degrades
// to plain text instead.
function explorerURLOrUndefined(
  chainID: bigint,
  address: Parameters<typeof getExplorerURL>[1],
  display: "address" | "token" = "address",
): string | undefined {
  try {
    return getExplorerURL(chainID, address, display, undefined);
  } catch (e) {
    console.warn("no explorer link for receiver", chainID, e);
    return undefined;
  }
}

function History() {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);
  const [swaps, setSwaps] = useState<SwapJSON[]>([]);
  const [assetDecimals, setAssetDecimals] = useState<Map<string, number>>(
    new Map<string, number>(),
  );

  useEffect(() => {
    let swapsJSON: Array<SwapJSON>;
    try {
      swapsJSON =
        TypedJSON.parseAsArray(localStorage.getItem("swaps"), SwapJSON) ||
        new Array<SwapJSON>();
    } catch (e) {
      console.error("swap history in localStorage is malformed, resetting...");
      swapsJSON = new Array<SwapJSON>();
    }
    setSwaps(swapsJSON);
    setDecimals(swapsJSON);
  }, []);

  const shortenAddress = (address: string) => {
    if (!address || address.length <= 10) return address;
    return `${address.substring(0, 4)}...${address.substring(
      address.length - 3,
    )}`;
  };

  const setDecimals = async (swaps: SwapJSON[]) => {
    const decMap = new Map<string, number>();
    const setAssetDec = async (
      asset: EthereumAsset | StellarAsset | NervosAsset,
    ) => {
      console.log(assetDecimals);
      if (!decMap.has(asset.code)) {
        try {
          const dec = await ctx.client.getDecimals(asset.toChannelAsset());
          if (dec) {
            decMap.set(asset.code, dec);
          }
        } catch (e) {
          console.log(e);
        }
      }
    };
    for (const swap of swaps) {
      console.log(swap);
      if (!swap.assets || !swap.assets[0] || !swap.assets[1]) continue;
      await Promise.all([
        setAssetDec(swap.assets[0]),
        setAssetDec(swap.assets[1]),
      ]);
    }
    setAssetDecimals(decMap);
  };

  // The decimals of an asset in the history. The backend is asked first, but
  // the history is read from localStorage and may be shown before the client
  // can answer, or the backend may not recognise an older entry's asset. A
  // missing value used to fall back to 0 decimals, which printed raw base
  // units (33300000000 CKB for 333 CKB), so the asset's own decimals and the
  // traded assets' known decimals come next, and an unknown amount is shown as
  // such rather than as a wrong number.
  const decimalsOf = (
    asset: EthereumAsset | StellarAsset | NervosAsset,
  ): number | undefined => {
    const fromBackend = assetDecimals.get(asset.code);
    if (fromBackend && fromBackend > 0) return fromBackend;
    if (asset instanceof NervosAsset && asset.decimal > 0) return asset.decimal;
    return knownDecimals.get(asset.code);
  };

  const getBalText = (
    swap: SwapJSON,
    index: 0 | 1,
  ): { balance: string; tokenCode: string } => {
    try {
      const decimals = decimalsOf(swap.assets[index]);
      return {
        balance:
          decimals === undefined
            ? t("unknownAmount")
            : formatBaseUnits(BigInt(swap.balances[index].valueOf()), decimals),
        tokenCode: getTokenCode(swap.assets[index]),
      };
    } catch (e) {
      console.log(e);
      return {
        balance: t("unknownAmount"),
        tokenCode: getTokenCode(swap.assets[index]),
      };
    }
  };

  return (
    <Container className={"HistoryContainer mt-2 w-75"} fluid>
      <div className={"ListViewWrapper"}>
        <Table>
          <thead>
            <tr>
              <th>{t("from")}</th>
              <th>{t("to")}</th>
              <th>{t("receiver")}</th>
              <th>{t("date")}</th>
            </tr>
          </thead>
          <tbody>
            {swaps
              .filter((swap) => swap.assets && swap.assets[0] && swap.assets[1])
              .slice()
              .reverse()
              .map((swap) => (
                <tr key={swap.swapID}>
                  <td>
                    {getBalText(swap, 0).balance}{" "}
                    {swap.assets![0] instanceof EthereumAsset ? (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={explorerURLOrUndefined(swap.assets![0].chainID, swap.assets![0].address, "token")}
                        style={{ color: "#00C9B8" }}
                      >
                        {getBalText(swap, 0).tokenCode}
                      </a>
                    ) : ( swap.assets![0] instanceof StellarAsset ?
                      (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={explorerURLOrUndefined(swap.assets![0].chainID, swap.assets![0].contractID, "token")}
                          style={{ color: "#00C9B8" }}
                        >
                          {getBalText(swap, 0).tokenCode}
                        </a>
                      ) : (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={explorerURLOrUndefined(BigInt(swap.assets![0].chainID), swap.assets![0].address.codeHash, "token")}
                            style={{ color: "#00C9B8" }}
                          >
                            {getBalText(swap, 0).tokenCode}
                          </a>
                        )
                    )}
                  </td>
                  <td>
                    {getBalText(swap, 1).balance}{" "}
                    {swap.assets![1] instanceof EthereumAsset ? (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={explorerURLOrUndefined(swap.assets![1].chainID, swap.assets![1].address, "token")}
                        style={{ color: "#00C9B8" }}
                      >
                        {getBalText(swap, 1).tokenCode}
                      </a>
                    ) : ( swap.assets![1] instanceof StellarAsset ?
                        (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={explorerURLOrUndefined(swap.assets![1].chainID, swap.assets![1].contractID, "token")}
                            style={{ color: "#00C9B8" }}
                          >
                            {getBalText(swap, 1).tokenCode}
                          </a>
                        ) : (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={explorerURLOrUndefined(BigInt(swap.assets![1].chainID), swap.assets![1].address.codeHash, "token")}
                            style={{ color: "#00C9B8" }}
                          >
                            {getBalText(swap, 1).tokenCode}
                          </a>
                        )
                    )}
                  </td>
                  {swap.receiverAddress ? (
                    <td className={"address"}>
                      {swap.assets![1] instanceof EthereumAsset &&
                      explorerURLOrUndefined(swap.assets![1].chainID, swap.receiverAddress) ? (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={explorerURLOrUndefined(
                            swap.assets![1].chainID,
                            swap.receiverAddress,
                          )}
                          style={{ color: "#00C9B8" }}
                        >
                          {shortenAddress(swap.receiverAddress.toString())}
                        </a>
                      ) : ( swap.assets![1] instanceof StellarAsset ?
                          (
                            <a
                              href={`https://stellar.expert/explorer/testnet/account/${swap.receiverAddress.toString()}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#00C9B8" }}
                            >
                              {shortenAddress(swap.receiverAddress.toString())}
                            </a>
                          ) : ( swap.assets![1] instanceof NervosAsset &&
                              explorerURLOrUndefined(swap.assets![1].chainID, swap.receiverAddress) ?
                              (
                                <a
                                  target="_blank"
                                  rel="noreferrer"
                                  href={explorerURLOrUndefined(
                                    swap.assets![1].chainID,
                                    swap.receiverAddress,
                                  )}
                                  style={{ color: "#00C9B8" }}
                                >
                                  {shortenAddress(swap.receiverAddress.toString())}
                                </a>
                              ) : (
                                <>
                                  {shortenAddress(swap.receiverAddress.toString())}
                                </>
                              )
                          )
                      )}
                    </td>
                  ) : (
                    <td>{t("you")}</td>
                  )}
                  {new Date(swap.date).toLocaleString() === "Invalid Date" ? (
                    <td>{t("unknown")}</td>
                  ) : (
                    <td>{new Date(swap.date).toLocaleString()}</td>
                  )}
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </Container>
  );
}

export default History;
